import React, { useState } from "react";
import pflLogoNoMargin from "../assets/tutorial/PfL-logo-no-margin-2025.svg";
import justMountains from "../assets/images/just mountains.svg";
import phoneImage from "../assets/images/phone-pexels-negativespace-33999.jpg";
import CTACard from "../componentLibrary/CTACard";
import InteractiveFeatureGrid from "../componentLibrary/InteractiveFeatureGrid";
import Header from "../components/Header";
import TopicsModal from "../components/TopicsModal";
import Modal from "../componentLibrary/Modal";
import ChallengeTopicsGrid from "../components/ChallengeTopicsGrid";
import PflStorySection from "../components/PflStorySection";
import HowItWorksSection from "../components/HowItWorksSection";

const BusinessLandingPage = () => {
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);

  // Business-specific topics for the modal
  const businessTopics = [
    {
      name: "Manage Stress",
      icon: "https://d2c85asnp7to29.cloudfront.net/1736194214726-Coming%20soon%20blue.svg",
      alt: "A megaphone with sound coming out of it",
    },
    {
      name: "Achieve Goals",
      icon: "https://d2c85asnp7to29.cloudfront.net/1727289084875-SMART%20Goals%20MAIN%20top%20of%20mountain%20and%20footsteps%20and%20flag%20artboard_top%20of%20mountain%2C%20footsteps%2C%20flag.svg",
      alt: "Footsteps leading to a flag at the top of a mountain",
    },
    {
      name: "Reduce Anxiety",
      icon: "https://d2c85asnp7to29.cloudfront.net/1728271665518-anxiety%20tiger%20with%20grass-01.svg",
      alt: "A tiger hiding in tall grass",
    },
    {
      name: "Reduce Burnout",
      icon: "https://d2c85asnp7to29.cloudfront.net/1728274116506-Burnout%20main%20icon%20person%20laying%20on%20desk%20with%20low%20battery%20sign%20above%20them_A.svg",
      alt: "A person asleep at a desk",
    },
    {
      name: "Address Conflict at Work",
      icon: "https://d2c85asnp7to29.cloudfront.net/1727551387443-Conflict%20effective%20communication-02.svg",
      alt: "Two people smiling at each other. A lightbulb representing an idea floats between them",
    },
    {
      name: "Overcome Imposter Syndrome",
      icon: "https://d2c85asnp7to29.cloudfront.net/1743712869992-imposter%20two%20people%20as%20one.png",
      alt: "Two People",
    },
    {
      name: "Improve Sleep",
      icon: "https://d2c85asnp7to29.cloudfront.net/1728261775304-Sleep%20moon%20bed%20and%20zzz_Sleep%20moon%2C%20bed%2C%20zzz.svg",
      alt: "A person asleep on the moon",
    },
    {
      name: "Give (and Receive) Useful Feedback",
      icon: "https://d2c85asnp7to29.cloudfront.net/1741734409713-Feedback%20main%20icon.svg",
      alt: "A text box with a circle around it",
    },
    {
      name: "Become an Active Listener",
      icon: "https://d2c85asnp7to29.cloudfront.net/1727551387443-Conflict%20effective%20communication-02.svg",
      alt: "Two people smiling at each other. A lightbulb representing an idea floats between them",
    },
    {
      name: "Create a Growth Mindset",
      icon: "https://d2c85asnp7to29.cloudfront.net/1738271342755-growth%20mindset%20tree.png",
      alt: "A tree",
    },
    {
      name: "Leverage the Power of Gratitude",
      icon: "https://d2c85asnp7to29.cloudfront.net/1728264280356-Sunset%20final%20gratitude%20tree_gratitude%20tree%209.svg",
      alt: "A hand holding a tree",
    },
    {
      name: "Manage Loss and Grief",
      icon: "https://d2c85asnp7to29.cloudfront.net/1727368265476-Grief%20main%20icon_grief%20main%20icon%20lower%20down.svg",
      alt: "A two-colored heart with a black-stroked wave down the middle of it and a bi-colored background for each half of the heart",
    },
  ];

  // Business-specific features for the interactive grid
  const businessFeatures = [
    {
      id: "root-causes",
      title: "Enables employees to address the root causes of challenges",
      description:
        "Goes beyond the surface symptoms to enable sustainable change rather than just temporary relief.",
    },
    {
      id: "strengthens-enterprise",
      title: "Strengthens your enterprise",
      description:
        "Helps your employees be present, engaged, focused, and successful at work.",
    },
    {
      id: "mastery-skills",
      title: "Builds broadly relevant mastery skills",
      description:
        "By learning how to manage their own everyday life challenges, your employees also become better at managing their work-related challenges.",
    },
    {
      id: "wide-range",
      title: "Covers a wide range of challenges",
      description:
        "Meets employees\nwhere they are today by providing practical life skills to address the variety of hurdles they face across domains of life.",
    },
    {
      id: "engagement",
      title: "Promotes engagement and personalization",
      description:
        "Your employees build custom, individualized toolboxes of support, to enhance their everyday use.",
    },
    {
      id: "ongoing-support",
      title: "Provides ongoing support",
      description:
        "Available to your employees 24/7, on demand, for when their needs change and they encounter new challenges.",
    },
    {
      id: "human-centered",
      title: "Fundamentally human-centered",
      description:
        "Curated by humans and communicated by humans. We value that your employees are human.",
    },
    {
      id: "evidence-based",
      title: "Always deeply evidence-based",
      description:
        "Built by leading scientists based on research by leading scientists.",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <Header transparent={false} />

      {/* Unified Hero Section */}
      <div className="relative overflow-visible bg-gradient-to-b from-white via-electrum-100 via-electrum-150 to-white">
        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-20 lg:pt-8">
          <h1
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            style={{ lineHeight: "1.5" }}
          >
            The Success of Your Organization Depends on the Well-Being of Your
            Employees.
            <br />
            Enter Psych for Life<sup>®</sup>.
          </h1>
        </div>

        {/* Mountains Image - Full Width Edge-to-Edge with CTA Card */}
        <div className="relative z-10 mb-0 flex items-center justify-center">
          {/* Mountain image positioned behind */}
          <img
            src={justMountains}
            alt="Mountain landscape"
            className="absolute bottom-20 left-0 transform -translate-y-1/2 w-full h-auto"
          />
          {/* CTA Card with Logo - Positioned in front of mountain */}
          <div className="relative z-10">
            <CTACard
              title={
                <>
                  Schedule a Meeting to Learn About Bringing Psych for Life
                  <sup>®</sup> to Your Organization and Employees
                </>
              }
              buttonText="Contact Our Team"
              onButtonClick={() => setShowBusinessModal(true)}
              width="lg"
              imageSrc={pflLogoNoMargin}
              imageAlt="Psych For Life logo"
            />
          </div>
        </div>

        {/* Second Paragraph */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto py-4 mb-6">
          <p className="text-lg md:text-xl text-gray-800 max-w-3xl mx-auto font-bold">
            Psych for Life<sup>®</sup> Equips Your Employees with
            Evidence-Based Knowledge and Skills to Navigate Everyday Challenges
            at Home and in Life, Paving the Way for Success at Work.
          </p>
        </div>

        {/* Challenge Areas Icons with Beach Background */}
        <ChallengeTopicsGrid onMoreClick={() => setShowTopicsModal(true)} />
      </div>

      {/* Why Psych for Life? Section */}
      <div className="relative z-10 py-20 bg-gradient-to-b from-transparent via-electrum-50 to-electrum-100">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-5xl font-bold text-electrum text-center mb-16">
            Why Psych for Life<sup>®</sup>?
          </h2>

          <div className="space-y-8 text-lg text-gray-800 max-w-4xl mx-auto">
            <p>
              You value your employees, as people and as the backbone of your
              organization. When they struggle with everyday challenges —
              stress, family, friendships — their well-being suffers and it
              affects them at work. You know the consequences:
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Reduced Productivity</li>
              <li>Negative Customer Interactions</li>
              <li>Conflicts with Co-Workers</li>
              <li>Burnout</li>
              <li>Increased absenteeism</li>
              <li>Low morale</li>
              <li>Higher employee turnover</li>
            </ul>

            <p>
              The well-being of your employees is paramount to the well-being of
              your organization. And more than ever, your employees need to see
              that you care.
            </p>

            <p>And you do.</p>

            <p>
              You understand that your employees need effective, evidence-based
              support as they navigate an increasingly stressful world.
            </p>

            <p>
              So perhaps you invest in simple apps to help your employees manage
              their stress in the moment. Or when their needs become more
              severe, you invest in (expensive) individualized therapy.
            </p>

            <p>
              What&apos;s missing, though, is the critical, foundational piece:
              Equipping your employees with tools to{" "}
              <strong>
                address the root causes of the challenges they routinely face
              </strong>{" "}
              and <strong>build enduring life skills</strong> to effectively
              navigate the unpredictable everyday obstacles, headwinds, poor
              footing, and sinkholes they frequently confront.
            </p>

            <p>
              When you invest in this foundational piece, rather than sinking
              deeper your employees develop the ability to <em>thrive</em>. And
              your organization thrives as well.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section Before What Psych for Life Does For Your Business */}
      <div className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <CTACard
              title={
                <>
                  Schedule a Meeting to Learn About Bringing Psych for Life
                  <sup>®</sup> to Your Organization and Employees
                </>
              }
              buttonText="Contact Our Team"
              onButtonClick={() => setShowBusinessModal(true)}
              width="lg"
              imageSrc={phoneImage}
              imageAlt="Phone with Psych For Life app"
            />
          </div>
        </div>
      </div>

      {/* What Psych For Life<sup>®</sup> Does For You? Section */}
      <div className="py-20 bg-gradient-to-b from-white via-electrum-50 to-electrum-100">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-5xl font-bold text-electrum text-center mb-16">
            What Psych for Life<sup>®</sup>
            <br />
            Does For Your Business
          </h2>

          {/* Interactive Feature Grid */}
          <InteractiveFeatureGrid features={businessFeatures} />
        </div>
      </div>

      {/* How It Works Section */}
      <HowItWorksSection
        title={
          <>
            How Psych for Life<sup>®</sup> Works for Your Employees
          </>
        }
        pageTitles={[
          <>
            Welcome to Psych for Life<sup>®</sup>!
          </>,
          "Learn Quickly",
          "Apply what you've learned",
          "Select and Customize",
          "Use!",
        ]}
      />

      {/* The Story Behind Psych For Life® Section */}
      <PflStorySection />

      {/* Final CTA Section */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold text-electrum mb-8">
            We&apos;re ready to work with you. Curious about the next steps?
          </h2>
          <p className="text-2xl text-gray-800 mb-12">
            Psych for Life<sup>®</sup> is a cost-effective, evidence-based,
            trustworthy solution for individuals and for the workplaces,
            providers, and institutions that aim to support them. By doing good
            for your employees, you can do well for your organization.
          </p>

          <div className="flex justify-center">
            <CTACard
              title={
                <>
                  Schedule a Meeting to Learn About Bringing Psych for Life
                  <sup>®</sup> to Your Organization and Employees
                </>
              }
              buttonText="Contact Our Team"
              onButtonClick={() => setShowBusinessModal(true)}
              width="lg"
              imageSrc={phoneImage}
              imageAlt="Phone with Psych For Life app"
            />
          </div>
        </div>
      </div>

      {/* Topics Modal */}
      <TopicsModal
        isOpen={showTopicsModal}
        onClose={() => setShowTopicsModal(false)}
        topics={businessTopics}
      />

      {/* Business Inquiry Modal */}
      <Modal
        isOpen={showBusinessModal}
        onCloseModal={() => setShowBusinessModal(false)}
        title="Contact Our Team"
        size="lg"
        height="80vh"
      >
        <div className="w-full h-full">
          <iframe
            src="https://mc5jf8-qft7x07vcws79ngwdhmp1.pub.sfmc-content.com/ge3byeykbgw"
            className="w-full h-full border-0"
            title="Business Inquiry Form"
            allow="clipboard-write"
          />
        </div>
      </Modal>
    </div>
  );
};

export default BusinessLandingPage;
